<script setup lang="ts">
import { onMounted, onUnmounted, ref, nextTick } from "vue";
import { LandingSiteRouteNames } from "@app/router/route-names.enum";
import { useLandingSiteStore } from "@app/stores/landing-site";
import { trackEvent } from "@app/composables/posthog";
import {
  getLandingVariant,
  type LandingVariant,
} from "@app/composables/landing-experiment";

// Control (current design) — the tree that stays baked into the prerendered static HTML.
import NavBar from "@app/components/landing-site/NavBar.vue";
import LandingSiteFooter from "@app/components/landing-site/LandingSiteFooter.vue";
import StickyMobileCta from "@app/components/landing-site/StickyMobileCta.vue";
import HeroSection from "@app/components/landing-site/sections/HeroSection.vue";
import VideoReelsSection from "@app/components/landing-site/sections/VideoReelsSection.vue";
import SocialProofSection from "@app/components/landing-site/sections/SocialProofSection.vue";
import AiSection from "@app/components/landing-site/sections/AiSection.vue";
import RoiCalculator from "@app/components/landing-site/sections/RoiCalculator.vue";
import FeaturesSection from "@app/components/landing-site/sections/FeaturesSection.vue";
import HowItWorksSection from "@app/components/landing-site/sections/HowItWorksSection.vue";
import PricingSection from "@app/components/landing-site/sections/PricingSection.vue";
import TestimonialsSection from "@app/components/landing-site/sections/TestimonialsSection.vue";
import ContactSection from "@app/components/landing-site/sections/ContactSection.vue";
import FinalCtaSection from "@app/components/landing-site/sections/FinalCtaSection.vue";

// The original page remains the SSR/hydration baseline; assignment is applied on mount.
import Beauty from "./Beauty.vue";

const landingSiteStore = useLandingSiteStore();
const mainRef = ref<HTMLElement>();
let observer: IntersectionObserver | null = null;
const variant = ref<LandingVariant>("control");

onMounted(async () => {
  variant.value = getLandingVariant();
  await nextTick();
  // Beauty records its own exposure after mounting. Never double-count treatment.
  if (variant.value === "control")
    trackEvent("landing_experience_viewed", { page: "/" });
  landingSiteStore.setCurrentPage(LandingSiteRouteNames.Home);

  if (variant.value !== "control" || !("IntersectionObserver" in window))
    return;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle("is-offscreen", !entry.isIntersecting);
      });
    },
    { rootMargin: "200px" }
  );

  mainRef.value?.querySelectorAll(".landing-section").forEach((section) => {
    observer!.observe(section);
  });
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<template>
  <!-- pb-24 on mobile keeps the footer clear of the sticky register CTA -->
  <Beauty v-if="variant === 'guided'" />
  <div
    v-else
    dir="rtl"
    class="h-full overflow-y-auto overflow-x-hidden bg-slate-50 font-sans text-slate-900 pb-24 lg:pb-0"
  >
    <NavBar />

    <main ref="mainRef">
      <template v-if="variant === 'control'">
        <HeroSection class="landing-section" />
        <VideoReelsSection class="landing-section" />
        <SocialProofSection class="landing-section" />
        <AiSection class="landing-section" />
        <RoiCalculator class="landing-section" />
        <FeaturesSection class="landing-section" />
        <HowItWorksSection class="landing-section" />
        <PricingSection class="landing-section" />
        <TestimonialsSection class="landing-section" />
        <ContactSection class="landing-section" />
        <FinalCtaSection class="landing-section" />
      </template>
    </main>

    <LandingSiteFooter />

    <StickyMobileCta />
  </div>
</template>
